import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { parseNumber } from 'helpers/core';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { MANAGE_SERVICES_TAB } from 'constants/logic';
import { intlMessage } from 'constants/propTypes';
import Message from 'components/Message';
import When from 'components/When';

import './ServicesPicker.scss';

const { block, element } = bem('services-picker');

const messages = defineMessages({
  title: {
    id: 'manage-services.components.services-picker.title',
    defaultMessage: 'Services',
  },
  duration: {
    id: 'manage-services.components.services-picker.duration',
    defaultMessage: '{duration} min',
  },
  addService: {
    id: 'manage-services.components.services-picker.add-service',
    defaultMessage: 'No service in the list? <a>Add new service to ClassPass</a>',
  },
});

export const ServicesPickerCheckboxes = React.forwardRef((props, ref) => {
  const { value = [], onChange, servicesList, ...inputProps } = props;
  // this replicates redux-form's format/parse props
  const handleChange = event => {
    const targetValue = parseNumber(event.target.value);
    const update = value.includes(targetValue) ? value.filter(id => id !== targetValue) : value.concat(targetValue);
    onChange(update);
  };
  return (
    <div className="grid">
      {(servicesList || []) &&
        servicesList.map(service => {
          const { id: serviceId, name: serviceName, duration: serviceDuration } = service;
          return (
            <div key={serviceId} className={element('checkbox', 'grid__item 1/1 md-1/2')}>
              <input
                ref={ref}
                {...inputProps}
                type="checkbox"
                id={serviceId}
                value={serviceId}
                onChange={handleChange}
                checked={value.includes(serviceId)}
                data-cypress="service-checkbox"
              />
              <label htmlFor={serviceId} className={element('info')}>
                <span className={element('name')}>{serviceName}</span>
                <Message className="text--light" message={messages.duration} values={{ duration: serviceDuration }} />
              </label>
            </div>
          );
        })}
    </div>
  );
});

ServicesPickerCheckboxes.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  servicesList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      duration: PropTypes.string,
    })
  ),
};

function ServicesPicker(props) {
  const { name, servicesList, description, venueId, hideAddLink = false } = props;
  const { control } = useFormContext();

  return (
    <div className={block()}>
      <Message as="label" className={element('label')} message={messages.title} />
      <Message as="div" className={element('description')} message={description} />
      <Controller
        control={control}
        name={name}
        render={({ field }) => <ServicesPickerCheckboxes {...field} servicesList={servicesList} />}
      />
      <When predicate={!hideAddLink}>
        <Message
          as="div"
          className="text--regular"
          message={messages.addService}
          values={{
            a: text => (
              <Link to={makePath.manageServicesAdd({ venueId, tab: MANAGE_SERVICES_TAB.SERVICES })}>{text}</Link>
            ),
          }}
        />
      </When>
    </div>
  );
}

ServicesPicker.propTypes = {
  venueId: PropTypes.number,
  name: PropTypes.string,
  description: intlMessage,
  servicesList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      duration: PropTypes.string,
    })
  ),
  hideAddLink: PropTypes.bool,
};

export default ServicesPicker;
